import {
  Autocomplete,
  Banner,
  Button,
  ButtonGroup,
  DropZone,
  FormLayout,
  Layout,
  Modal,
  Page,
  Text,
  TextField,
  VerticalStack,
  Box,
  LegacyCard,
} from '@shopify/polaris';
import { CancelMinor, DeleteMinor, EditMinor, SaveMinor, NoteMinor, DuplicateMinor } from '@shopify/polaris-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useQuery } from 'react-query';
import { getFilters, uploadProductImages } from '../../../services/productService';
import { composeSku, validateProductForm, SKU_SUFFIX_PATTERN } from '../../../helpers/productValidation';
import ProductTagsField from './ProductTagsField';
import CopyFromSeriesModal from './CopyFromSeriesModal';

/** Id local estable para las filas de variante mientras el producto no existe en BD. */
let uidCounter = 0;
const nextUid = () => {
  uidCounter += 1;
  return `v${uidCounter}`;
};

const emptyVariant = () => ({
  _uid: nextUid(),
  option: '',
  skuSuffix: '',
  barcode: '',
  upcEph: '',
  pvp: '',
  cost: '',
});

const ProductForm = ({
  initialData,
  onSubmit,
  isLoading,
  isNew = false,
  title,
  subtitle,
  backAction,
  /** Errores devueltos por el servidor, ya mapeados a `{ campo: mensaje }`. */
  serverErrors = {},
}) => {
  // ---------- Carga de filtros para autocompletado ----------
  const { data: filtersData } = useQuery(['productFilters'], () => getFilters().then((r) => r.data), {
    staleTime: 1000 * 60 * 5, // 5 minutos de cache
  });

  const getOptions = useCallback(
    (slug) => {
      const filter = filtersData?.filters?.find((f) => f.slug === slug);
      if (!filter?.options) return [];
      return filter.options.map((opt) => ({ label: String(opt), value: String(opt) }));
    },
    [filtersData],
  );

  // ---------- Estado del formulario ----------
  const [form, setForm] = useState({
    title: '',
    description: '',
    family: '',
    gender: '',
    category: '',
    subcategory: '',
    season: '',
    year: '',
    images: [],
    tags: [],
    detail: '',
    pvp: '', // Valor por defecto para las variantes nuevas
    cost: '', // Valor por defecto para las variantes nuevas
    variants: [],
  });

  /** Errores de cliente. Solo se pintan tras el primer intento de guardar. */
  const [localErrors, setLocalErrors] = useState({});
  const [hasTriedSubmit, setHasTriedSubmit] = useState(false);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copiedFrom, setCopiedFrom] = useState(null);

  useEffect(() => {
    if (initialData) {
      setForm({
        ...initialData,
        title: initialData.title || '',
        description: initialData.description || '',
        family: initialData.family || '',
        gender: initialData.gender || '',
        category: initialData.category || '',
        subcategory: initialData.subcategory || '',
        season: initialData.season || '',
        year: initialData.year ?? '',
        images: initialData.images || [],
        tags: initialData.tags || [],
        detail:
          initialData.detail ||
          (Array.isArray(initialData.details) ? initialData.details.join('<br>') : initialData.details || ''),
        variants: initialData.variants || [],
      });
    }
  }, [initialData]);

  const handleChange = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // ---------- Autocompletado ----------
  const [autocompleteStates, setAutocompleteStates] = useState({
    gender: '',
    category: '',
    subcategory: '',
    season: '',
    year: '',
  });

  /**
   * En alta, el SKU se compone: prefijo = familia, sufijo = 3 dígitos que
   * teclea el usuario. Así cambiar la familia reescribe todos los SKU de golpe
   * y nunca queda uno con el prefijo de otra serie (que haría que Hopper
   * duplicase el producto en Shopify).
   */
  const variantsWithSku = useMemo(
    () =>
      (form.variants || []).map((variant) => ({
        ...variant,
        sku: isNew ? composeSku(form.family, variant.skuSuffix) : variant.sku,
      })),
    [form.variants, form.family, isNew],
  );

  /** Errores de cliente + los que devolvió el servidor. */
  const errors = useMemo(() => ({ ...localErrors, ...serverErrors }), [localErrors, serverErrors]);
  const showErrors = hasTriedSubmit || Object.keys(serverErrors).length > 0;
  const errorFor = (field) => (showErrors ? errors[field] : undefined);

  // ---------- Copiar de otra serie ----------
  const handleCopyFromSeries = (source) => {
    setForm((prev) => ({
      ...prev,
      title: source.title || '',
      description: source.description || '',
      gender: source.gender || '',
      category: source.category || '',
      subcategory: source.subcategory || '',
      season: source.season || '',
      year: source.year ?? '',
      images: source.images || [],
      tags: source.tags || [],
      detail:
        source.detail || (Array.isArray(source.details) ? source.details.join('<br>') : source.details || ''),
      pvp: source.pvp_min ?? prev.pvp,
      cost: source.cost_min ?? prev.cost,
      // La familia NO se copia: es lo que distingue al producto nuevo.
      // De cada variante se hereda la talla y los precios; el EAN, el UPC y el
      // sufijo del SKU van vacíos porque son identificadores únicos.
      variants: (source.variants || []).map((variant) => ({
        _uid: nextUid(),
        option: variant.title || variant.option || '',
        skuSuffix: '',
        barcode: '',
        upcEph: '',
        pvp: variant.pvp ?? source.pvp_min ?? '',
        cost: variant.cost ?? source.cost_min ?? '',
      })),
    }));
    setCopiedFrom(source.family || null);
    setAutocompleteStates({
      gender: source.gender || '',
      category: source.category || '',
      subcategory: source.subcategory || '',
      season: source.season || '',
      year: source.year ? String(source.year) : '',
    });
  };

  // ---------- Imágenes ----------
  // OJO: en alta NO se auto-guarda. Antes cualquier toque a las fotos disparaba
  // un `save_product`, que en un producto que aún no existe se traducía en un
  // POST /products con solo `{images}` y un 400 en la cara del usuario.
  const persistImagesIfEditing = useCallback(
    (images) => {
      if (isNew) return;
      onSubmit({ type: 'save_product', data: { images } });
    },
    [isNew, onSubmit],
  );

  const handleRemoveImage = (idx) => {
    setForm((prev) => {
      const images = prev.images.filter((_, i) => i !== idx);
      persistImagesIfEditing(images);
      return { ...prev, images };
    });
  };

  const handleDrop = useCallback(
    async (_droppedFiles, acceptedFiles) => {
      if (acceptedFiles.length === 0) return;
      try {
        const response = await uploadProductImages(acceptedFiles);
        const newUrls = response.data.data;
        setForm((prev) => {
          const images = [...(prev.images || []), ...newUrls];
          persistImagesIfEditing(images);
          return { ...prev, images };
        });
      } catch (error) {
        console.error('Error uploading images:', error);
      }
    },
    [persistImagesIfEditing],
  );

  const onDragEnd = (result) => {
    if (!result.destination) return;
    setForm((prev) => {
      const images = Array.from(prev.images);
      const [reordered] = images.splice(result.source.index, 1);
      images.splice(result.destination.index, 0, reordered);
      persistImagesIfEditing(images);
      return { ...prev, images };
    });
  };

  // ---------- Handlers Variantes ----------
  const [editingVariants, setEditingVariants] = useState({});
  const [newVariantModalOpen, setNewVariantModalOpen] = useState(false);
  const defaultNewVariant = {
    sku: '',
    option: '',
    barcode: '',
    upcEph: '',
    pvp: initialData?.pvp_min ?? '',
    cost: initialData?.cost_min ?? '',
    bookValue: initialData?.bookValue_min ?? '',
  };
  const [newVariant, setNewVariant] = useState(defaultNewVariant);

  const startEditingVariant = (variant) => {
    setEditingVariants((prev) => ({
      ...prev,
      [variant.id]: {
        sku: variant.sku || '',
        option: variant.title || '',
        barcode: variant.barcode || '',
        upcEph: variant.upcEph || '',
        pvp: variant.pvp ?? '',
        cost: variant.cost ?? '',
        bookValue: variant.bookValue ?? '',
      },
    }));
  };

  const cancelEditingVariant = (id) => {
    setEditingVariants((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleVariantFieldChange = (id, field) => (value) => {
    setEditingVariants((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  /** Edición directa de una fila en modo alta (no hay ida y vuelta al servidor). */
  const updateLocalVariant = (uid, field) => (value) => {
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.map((v) => (v._uid === uid ? { ...v, [field]: value } : v)),
    }));
  };

  const handleCreateVariant = () => {
    const data = { ...newVariant };
    ['pvp', 'cost', 'bookValue'].forEach((f) => {
      if (data[f] !== '' && data[f] !== undefined) data[f] = Number(data[f]);
      else delete data[f];
    });

    onSubmit({ type: 'create_variant', data });
    setNewVariantModalOpen(false);
    setNewVariant(defaultNewVariant);
  };

  const handleAddLocalVariant = () => {
    setForm((prev) => ({
      ...prev,
      variants: [...prev.variants, { ...emptyVariant(), pvp: prev.pvp ?? '', cost: prev.cost ?? '' }],
    }));
  };

  const handleDeleteLocalVariant = (uid) => {
    setForm((prev) => ({ ...prev, variants: prev.variants.filter((v) => v._uid !== uid) }));
  };

  const handleDeleteVariant = (variantId, sku) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar la variante ${sku}?`)) {
      onSubmit({ type: 'delete_variant', variantId });
    }
  };

  const saveVariantRemote = (variantId) => {
    const data = { ...editingVariants[variantId] };
    ['pvp', 'cost', 'bookValue'].forEach((f) => {
      if (data[f] !== '' && data[f] !== undefined) data[f] = Number(data[f]);
    });
    onSubmit({ type: 'update_variant', variantId, data });
    cancelEditingVariant(variantId);
  };

  // ---------- Submit General ----------
  const handleGeneralSubmit = () => {
    const candidate = { ...form, variants: variantsWithSku };

    if (isNew) {
      const found = validateProductForm(candidate);
      setLocalErrors(found);
      setHasTriedSubmit(true);
      if (Object.keys(found).length > 0) return;
    }

    // eslint-disable-next-line no-unused-vars
    const { variants, _uid, ...rest } = candidate;
    const payload = { ...rest };

    if (payload.year) payload.year = Number(payload.year);
    if (payload.pvp !== '' && payload.pvp !== undefined) payload.pvp = Number(payload.pvp);
    else delete payload.pvp;
    if (payload.cost !== '' && payload.cost !== undefined) payload.cost = Number(payload.cost);
    else delete payload.cost;

    if (isNew) {
      // En alta las variantes viajan DENTRO del producto: el back las exige
      // (`variants.min(1).required()`) y las crea en la misma transacción.
      // Antes se descartaban aquí y el POST fallaba siempre con un 400.
      payload.variants = variantsWithSku.map((variant) => ({
        sku: variant.sku,
        option: variant.option,
        barcode: String(variant.barcode).trim(),
        ...(String(variant.upcEph || '').trim() ? { upcEph: String(variant.upcEph).trim() } : {}),
        pvp: Number(variant.pvp),
        cost: Number(variant.cost),
      }));
    }

    onSubmit({ type: 'save_product', data: payload });
  };

  const errorCount = showErrors ? Object.keys(errors).length : 0;

  return (
    <Page
      title={title}
      subtitle={subtitle}
      backAction={backAction}
      divider
      secondaryActions={
        isNew ? [{ content: 'Copiar de otra serie', icon: DuplicateMinor, onAction: () => setCopyModalOpen(true) }] : []
      }
    >
      <Layout>
        {/* SECCIÓN 1: DATOS GENERALES */}
        <Layout.Section>
          <VerticalStack gap="4">
            {errorCount > 0 && (
              <Box padding="4" paddingBlockEnd="0">
                <Banner status="critical" title="Revisa el formulario">
                  <p>
                    Hay {errorCount} {errorCount === 1 ? 'campo con errores' : 'campos con errores'}. No se ha creado
                    nada todavía: corrige lo marcado en rojo y vuelve a intentarlo.
                  </p>
                </Banner>
              </Box>
            )}

            {copiedFrom && (
              <Box padding="4" paddingBlockEnd="0">
                <Banner status="info" onDismiss={() => setCopiedFrom(null)}>
                  Datos copiados de la serie <b>{copiedFrom}</b>. Falta ponerle familia, y el EAN y el SKU de cada talla.
                </Banner>
              </Box>
            )}

            <Box padding="4">
              <VerticalStack gap="2">
                <Text variant="headingMd" as="h2">
                  Datos del producto
                </Text>
                <Text variant="bodyMd" color="subdued">
                  Información general heredada por las variantes.
                </Text>
                <LegacyCard>
                  <LegacyCard.Section>
                    <FormLayout>
                      <TextField
                        label="Título"
                        value={form.title}
                        onChange={handleChange('title')}
                        autoComplete="off"
                        requiredIndicator
                        error={errorFor('title')}
                      />
                      <TextField
                        label="Descripción"
                        value={form.description}
                        onChange={handleChange('description')}
                        multiline={3}
                        autoComplete="off"
                        error={errorFor('description')}
                      />
                      <FormLayout.Group>
                        <TextField
                          label="Familia"
                          value={form.family}
                          onChange={handleChange('family')}
                          autoComplete="off"
                          requiredIndicator
                          placeholder="00374-1200"
                          helpText={isNew ? 'Formato AAAAA-BBBB. Es el prefijo de todos los SKU.' : undefined}
                          error={errorFor('family')}
                        />
                        <AutocompleteField
                          label="Género"
                          field="gender"
                          options={getOptions('genders')}
                          value={form.gender}
                          searchValue={autocompleteStates.gender}
                          onSelect={(val) => {
                            setForm((prev) => ({ ...prev, gender: val }));
                            setAutocompleteStates((prev) => ({ ...prev, gender: val }));
                          }}
                          onSearchChange={(val) => {
                            setAutocompleteStates((prev) => ({ ...prev, gender: val }));
                            if (val === '') setForm((prev) => ({ ...prev, gender: '' }));
                          }}
                          onBlur={() => {
                            if (autocompleteStates.gender && !form.gender) {
                              setForm((prev) => ({ ...prev, gender: autocompleteStates.gender }));
                            }
                          }}
                          required
                          error={errorFor('gender')}
                        />
                      </FormLayout.Group>
                      <FormLayout.Group>
                        <AutocompleteField
                          label="Categoría"
                          field="category"
                          options={getOptions('categories')}
                          value={form.category}
                          searchValue={autocompleteStates.category}
                          onSelect={(val) => {
                            setForm((prev) => ({ ...prev, category: val }));
                            setAutocompleteStates((prev) => ({ ...prev, category: val }));
                          }}
                          onSearchChange={(val) => {
                            setAutocompleteStates((prev) => ({ ...prev, category: val }));
                            if (val === '') setForm((prev) => ({ ...prev, category: '' }));
                          }}
                          onBlur={() => {
                            if (autocompleteStates.category && !form.category) {
                              setForm((prev) => ({ ...prev, category: autocompleteStates.category }));
                            }
                          }}
                          required
                          error={errorFor('category')}
                        />
                        <AutocompleteField
                          label="Subcategoría"
                          field="subcategory"
                          options={getOptions('subcategories')}
                          value={form.subcategory}
                          searchValue={autocompleteStates.subcategory}
                          onSelect={(val) => {
                            setForm((prev) => ({ ...prev, subcategory: val }));
                            setAutocompleteStates((prev) => ({ ...prev, subcategory: val }));
                          }}
                          onSearchChange={(val) => {
                            setAutocompleteStates((prev) => ({ ...prev, subcategory: val }));
                            if (val === '') setForm((prev) => ({ ...prev, subcategory: '' }));
                          }}
                          onBlur={() => {
                            if (autocompleteStates.subcategory && !form.subcategory) {
                              setForm((prev) => ({ ...prev, subcategory: autocompleteStates.subcategory }));
                            }
                          }}
                          error={errorFor('subcategory')}
                        />
                      </FormLayout.Group>
                      <FormLayout.Group>
                        <AutocompleteField
                          label="Temporada"
                          field="season"
                          options={getOptions('seasons')}
                          value={form.season}
                          searchValue={autocompleteStates.season}
                          onSelect={(val) => {
                            setForm((prev) => ({ ...prev, season: val }));
                            setAutocompleteStates((prev) => ({ ...prev, season: val }));
                          }}
                          onSearchChange={(val) => {
                            setAutocompleteStates((prev) => ({ ...prev, season: val }));
                            if (val === '') setForm((prev) => ({ ...prev, season: '' }));
                          }}
                          onBlur={() => {
                            if (autocompleteStates.season && !form.season) {
                              setForm((prev) => ({ ...prev, season: autocompleteStates.season }));
                            }
                          }}
                          required
                          error={errorFor('season')}
                        />
                        <AutocompleteField
                          label="Año"
                          field="year"
                          options={getOptions('years')}
                          value={form.year}
                          searchValue={autocompleteStates.year}
                          onSelect={(val) => {
                            setForm((prev) => ({ ...prev, year: val }));
                            setAutocompleteStates((prev) => ({ ...prev, year: val }));
                          }}
                          onSearchChange={(val) => {
                            setAutocompleteStates((prev) => ({ ...prev, year: val }));
                            if (val === '') setForm((prev) => ({ ...prev, year: '' }));
                          }}
                          onBlur={() => {
                            if (autocompleteStates.year && !form.year) {
                              setForm((prev) => ({ ...prev, year: autocompleteStates.year }));
                            }
                          }}
                          required
                          error={errorFor('year')}
                        />
                      </FormLayout.Group>
                      {isNew && (
                        <FormLayout.Group>
                          <TextField
                            label="PVP por defecto"
                            type="number"
                            step="0.01"
                            value={String(form.pvp)}
                            onChange={handleChange('pvp')}
                            autoComplete="off"
                            helpText="Se usa al añadir tallas nuevas. El precio real es el de cada talla."
                          />
                          <TextField
                            label="Coste por defecto"
                            type="number"
                            step="0.01"
                            value={String(form.cost)}
                            onChange={handleChange('cost')}
                            autoComplete="off"
                            helpText="Se usa al añadir tallas nuevas. El coste real es el de cada talla."
                          />
                        </FormLayout.Group>
                      )}
                      <ProductTagsField value={form.tags} onChange={handleChange('tags')} />
                      <Box>
                        <Text variant="labelMd" as="p">
                          Detalles (HTML)
                        </Text>
                        <Box paddingBlockStart="2">
                          <ReactQuill
                            theme="snow"
                            value={form.detail}
                            onChange={handleChange('detail')}
                            style={{ height: '200px', marginBottom: '50px' }}
                          />
                        </Box>
                      </Box>
                    </FormLayout>
                    <Box paddingBlockStart="4">
                      <Button primary onClick={handleGeneralSubmit} loading={isLoading}>
                        {isNew ? 'Crear Producto' : 'Guardar Cambios generales'}
                      </Button>
                    </Box>
                  </LegacyCard.Section>
                </LegacyCard>
              </VerticalStack>
            </Box>

            {/* SECCIÓN 2: IMÁGENES */}
            <Box padding="4">
              <VerticalStack gap="4">
                <Text variant="headingMd" as="h2">
                  Imágenes
                </Text>
                <Text variant="bodySm" color="subdued">
                  Arrastra imágenes para reordenar. Suelta archivos en cualquier parte para subir. No son obligatorias.
                </Text>
                <LegacyCard>
                  <LegacyCard.Section>
                    <Box position="relative">
                      <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="product-images" direction="horizontal">
                          {(provided) => (
                            <div
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                              className="flex gap-4 p-2 overflow-x-auto min-h-[120px]"
                            >
                              {form.images.map((img, idx) => (
                                <Draggable key={`${img}-${idx}`} draggableId={`${img}-${idx}`} index={idx}>
                                  {(provided) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      className="relative flex-shrink-0 group"
                                    >
                                      <Box
                                        width="100px"
                                        background="bg-subdued"
                                        borderRadius="2"
                                        overflow="hidden"
                                        shadow="sm"
                                      >
                                        {img ? (
                                          <img
                                            src={img}
                                            alt="Thumbnail"
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                          />
                                        ) : (
                                          <div className="flex items-center justify-center h-full">
                                            <NoteMinor className="w-8 h-8 text-gray-300" />
                                          </div>
                                        )}
                                      </Box>
                                      <div className="absolute top-[-8px] right-[-8px] opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Button
                                          icon={DeleteMinor}
                                          size="slim"
                                          destructive
                                          outline
                                          onClick={() => handleRemoveImage(idx)}
                                        />
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                              <VerticalStack align="center" justify="center" gap="2" padding="4" height="100%">
                                <Box
                                  width="100px"
                                  background="bg-subdued"
                                  borderRadius="2"
                                  overflow="hidden"
                                  shadow="sm"
                                >
                                  <DropZone
                                    onDrop={handleDrop}
                                    accept="image/jpeg"
                                    allowMultiple
                                    overlay="Añadir imágenes"
                                    overlayText="Suelta las imágenes para subirlas"
                                    id="full-screen-dropzone"
                                    dropOnPage
                                    style={{ height: '100%' }}
                                  >
                                    <DropZone.FileUpload style={{ height: '100%' }} />
                                  </DropZone>
                                </Box>
                              </VerticalStack>
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    </Box>
                  </LegacyCard.Section>
                </LegacyCard>
              </VerticalStack>
            </Box>

            {/* SECCIÓN 3: VARIANTES */}
            <Box padding="4" shadow="sm">
              <VerticalStack gap="4">
                <div className="flex justify-between items-center">
                  <div>
                    <Text variant="headingMd" as="h2">
                      Variantes
                    </Text>
                    <Text variant="bodySm" color="subdued">
                      {isNew
                        ? 'Cada talla necesita SKU, EAN, precio y coste. El UPC es opcional.'
                        : 'Gestiona las variantes individuales.'}
                    </Text>
                  </div>
                  <Button onClick={isNew ? handleAddLocalVariant : () => setNewVariantModalOpen(true)}>
                    Añadir variante
                  </Button>
                </div>
                {showErrors && errors.variants && (
                  <Banner status="critical">
                    <p>{errors.variants}</p>
                  </Banner>
                )}
                <LegacyCard>
                  <LegacyCard.Section>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 px-1 font-semibold">Opción</th>
                            <th className="text-left py-2 px-1 font-semibold">SKU</th>
                            <th className="text-left py-2 px-1 font-semibold">EAN</th>
                            <th className="text-left py-2 px-1 font-semibold">UPC EPH</th>
                            <th className="text-right py-2 px-1 font-semibold">PVP</th>
                            <th className="text-right py-2 px-1 font-semibold">Coste</th>
                            <th className="text-center py-2 px-1 font-semibold">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isNew
                            ? variantsWithSku.map((variant, index) => (
                                <NewVariantRow
                                  key={variant._uid}
                                  variant={variant}
                                  index={index}
                                  family={form.family}
                                  onChange={updateLocalVariant}
                                  onDelete={() => handleDeleteLocalVariant(variant._uid)}
                                  errorFor={errorFor}
                                />
                              ))
                            : form.variants.map((variant) => {
                                const vId = variant.id;
                                const isEditing = !!editingVariants[vId];
                                const ed = editingVariants[vId];

                                return (
                                  <tr key={vId} className="border-b border-gray-100 hover:bg-gray-50">
                                    {isEditing ? (
                                      <>
                                        <td className="py-1 px-1">
                                          <input
                                            value={ed.option}
                                            onChange={(e) => handleVariantFieldChange(vId, 'option')(e.target.value)}
                                            className="w-full border rounded px-1"
                                          />
                                        </td>
                                        <td className="py-1 px-1">
                                          <input
                                            value={ed.sku}
                                            onChange={(e) => handleVariantFieldChange(vId, 'sku')(e.target.value)}
                                            className="w-full border rounded px-1"
                                          />
                                        </td>
                                        <td className="py-1 px-1">
                                          <input
                                            value={ed.barcode}
                                            onChange={(e) => handleVariantFieldChange(vId, 'barcode')(e.target.value)}
                                            className="w-full border rounded px-1"
                                          />
                                        </td>
                                        <td className="py-1 px-1">
                                          <input
                                            value={ed.upcEph}
                                            onChange={(e) => handleVariantFieldChange(vId, 'upcEph')(e.target.value)}
                                            className="w-full border rounded px-1"
                                          />
                                        </td>
                                        <td className="py-1 px-1 text-right">
                                          <input
                                            type="number"
                                            value={ed.pvp}
                                            onChange={(e) => handleVariantFieldChange(vId, 'pvp')(e.target.value)}
                                            className="w-16 border rounded px-1 text-right"
                                          />
                                        </td>
                                        <td className="py-1 px-1 text-right">
                                          <input
                                            type="number"
                                            value={ed.cost}
                                            onChange={(e) => handleVariantFieldChange(vId, 'cost')(e.target.value)}
                                            className="w-16 border rounded px-1 text-right"
                                          />
                                        </td>
                                        <td className="py-2 px-1 text-right flex justify-center">
                                          <ButtonGroup align="right" spacing="tight">
                                            <Button
                                              size="slim"
                                              plain
                                              icon={SaveMinor}
                                              onClick={() => saveVariantRemote(vId)}
                                            />
                                            <Button
                                              size="slim"
                                              plain
                                              icon={CancelMinor}
                                              onClick={() => cancelEditingVariant(vId)}
                                            />
                                          </ButtonGroup>
                                        </td>
                                      </>
                                    ) : (
                                      <>
                                        <td className="py-2 px-1">{variant.title}</td>
                                        <td className="py-2 px-1">{variant.sku}</td>
                                        <td className="py-2 px-1">{variant.barcode}</td>
                                        <td className="py-2 px-1">{variant.upcEph || '—'}</td>
                                        <td className="py-2 px-1 text-right">{variant.pvp?.toFixed(2) || '—'}</td>
                                        <td className="py-2 px-1 text-right">{variant.cost?.toFixed(2) || '—'}</td>
                                        <td className="py-2 px-1 text-right flex justify-center">
                                          <ButtonGroup align="right" spacing="tight">
                                            <Button
                                              plain
                                              icon={EditMinor}
                                              size="slim"
                                              onClick={() => startEditingVariant(variant)}
                                            />
                                            <Button
                                              icon={DeleteMinor}
                                              plain
                                              destructive
                                              onClick={() => handleDeleteVariant(variant.id, variant.sku)}
                                            />
                                          </ButtonGroup>
                                        </td>
                                      </>
                                    )}
                                  </tr>
                                );
                              })}
                          {form.variants.length === 0 && (
                            <tr>
                              <td colSpan={7} className="py-4 px-1 text-center">
                                <Text variant="bodySm" color="subdued">
                                  Todavía no hay variantes. Añade al menos una talla.
                                </Text>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </LegacyCard.Section>
                </LegacyCard>
              </VerticalStack>
            </Box>
          </VerticalStack>
        </Layout.Section>
      </Layout>

      {/* MODAL NUEVA VARIANTE (solo en edición: en alta las filas se editan en la tabla) */}
      {newVariantModalOpen && (
        <Modal
          open={newVariantModalOpen}
          onClose={() => setNewVariantModalOpen(false)}
          title="Añadir variante"
          primaryAction={{ content: 'Añadir', onAction: handleCreateVariant }}
        >
          <Modal.Section>
            <FormLayout>
              <TextField
                label="Opción"
                value={newVariant.option}
                onChange={(v) => setNewVariant({ ...newVariant, option: v })}
                autoComplete="off"
              />
              <TextField
                label="SKU"
                value={newVariant.sku}
                onChange={(v) => setNewVariant({ ...newVariant, sku: v })}
                autoComplete="off"
                helpText={form.family ? `Debe empezar por ${form.family}-` : undefined}
              />
              <TextField
                label="EAN"
                value={newVariant.barcode}
                onChange={(v) => setNewVariant({ ...newVariant, barcode: v })}
                autoComplete="off"
              />
              <TextField
                label="UPC EPH"
                value={newVariant.upcEph}
                onChange={(v) => setNewVariant({ ...newVariant, upcEph: v })}
                autoComplete="off"
              />
              <FormLayout.Group>
                <TextField
                  label="PVP"
                  type="number"
                  value={String(newVariant.pvp)}
                  onChange={(v) => setNewVariant({ ...newVariant, pvp: v })}
                  autoComplete="off"
                />
                <TextField
                  label="Coste"
                  type="number"
                  value={String(newVariant.cost)}
                  onChange={(v) => setNewVariant({ ...newVariant, cost: v })}
                  autoComplete="off"
                />
              </FormLayout.Group>
            </FormLayout>
          </Modal.Section>
        </Modal>
      )}

      <CopyFromSeriesModal
        open={copyModalOpen}
        onClose={() => setCopyModalOpen(false)}
        onCopy={handleCopyFromSeries}
      />
    </Page>
  );
};

export default ProductForm;

/** Celda de tabla con estado de error visible. */
const CellInput = ({ error, className = '', ...props }) => (
  <>
    <input
      {...props}
      className={`w-full border rounded px-1 ${error ? 'border-red-500 bg-red-50' : ''} ${className}`}
    />
    {error && (
      <div className="pt-1">
        <Text variant="bodySm" color="critical">
          {error}
        </Text>
      </div>
    )}
  </>
);

/**
 * Fila de variante en modo alta: siempre editable.
 *
 * En edición hay que pulsar el lápiz para editar cada fila, pero en un alta —y
 * sobre todo justo después de "copiar de otra serie", que deja los EAN vacíos—
 * obligar a abrir fila por fila sería insufrible.
 */
const NewVariantRow = ({ variant, index, family, onChange, onDelete, errorFor }) => {
  const at = (field) => `variants[${index}].${field}`;

  return (
    <tr className="border-b border-gray-100 align-top">
      <td className="py-1 px-1">
        <CellInput
          value={variant.option}
          onChange={(e) => onChange(variant._uid, 'option')(e.target.value)}
          placeholder="48"
          error={errorFor(at('option'))}
        />
      </td>
      <td className="py-1 px-1">
        <div className="flex items-center gap-1">
          <span className="text-gray-500 whitespace-nowrap">{family || 'AAAAA-BBBB'}-</span>
          <CellInput
            value={variant.skuSuffix || ''}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, '').slice(0, 3);
              onChange(variant._uid, 'skuSuffix')(digits);
            }}
            placeholder="036"
            inputMode="numeric"
            className="w-16"
            error={errorFor(at('sku'))}
          />
        </div>
        {!errorFor(at('sku')) && variant.skuSuffix && !SKU_SUFFIX_PATTERN.test(variant.skuSuffix) && (
          <Text variant="bodySm" color="subdued">
            3 dígitos
          </Text>
        )}
      </td>
      <td className="py-1 px-1">
        <CellInput
          value={variant.barcode}
          onChange={(e) => onChange(variant._uid, 'barcode')(e.target.value.replace(/\D/g, '').slice(0, 13))}
          placeholder="8445678901235"
          inputMode="numeric"
          error={errorFor(at('barcode'))}
        />
      </td>
      <td className="py-1 px-1">
        <CellInput
          value={variant.upcEph || ''}
          onChange={(e) => onChange(variant._uid, 'upcEph')(e.target.value)}
          placeholder="opcional"
          error={errorFor(at('upcEph'))}
        />
      </td>
      <td className="py-1 px-1 text-right">
        <CellInput
          type="number"
          step="0.01"
          value={variant.pvp}
          onChange={(e) => onChange(variant._uid, 'pvp')(e.target.value)}
          className="w-20 text-right"
          error={errorFor(at('pvp'))}
        />
      </td>
      <td className="py-1 px-1 text-right">
        <CellInput
          type="number"
          step="0.01"
          value={variant.cost}
          onChange={(e) => onChange(variant._uid, 'cost')(e.target.value)}
          className="w-20 text-right"
          error={errorFor(at('cost'))}
        />
      </td>
      <td className="py-2 px-1 text-center">
        <Button icon={DeleteMinor} plain destructive onClick={onDelete} accessibilityLabel="Eliminar variante" />
      </td>
    </tr>
  );
};

const AutocompleteField = ({
  label,
  options,
  value,
  searchValue,
  onSelect,
  onSearchChange,
  onBlur,
  required = false,
  error,
}) => {
  const filteredOptions = useMemo(() => {
    const search = (String(searchValue) || '').toLowerCase();
    return options.filter((opt) => String(opt.label).toLowerCase().includes(search));
  }, [options, searchValue]);

  return (
    <Autocomplete
      options={filteredOptions}
      selected={[value]}
      onSelect={(selected) => onSelect(selected[0])}
      textField={
        <Autocomplete.TextField
          label={label}
          value={searchValue || value || ''}
          onChange={onSearchChange}
          autoComplete="off"
          requiredIndicator={required}
          onBlur={onBlur}
          error={error}
        />
      }
    />
  );
};
